// Modal grid for an action button whose handler is 'select_grid'. The action's
// business logic returned candidate rows; the user ticks the ones to keep and
// confirms. The selected rows are applied like a multi-row item_change
// (unselected rows are ignored).

import { useMemo, useState } from 'react'
import { tw } from '../testHooks'

interface Props {
  title?: string
  rows: Array<Record<string, unknown>>
  // Optional explicit column order; otherwise derived from the rows' keys.
  columns?: string[]
  // Column headings by row key (the target form's column headings). A key
  // with none is shown humanised ("QTY_RECEIVED" → "Qty received").
  labels?: Record<string, string>
  onCancel: () => void
  onApply: (selected: Array<Record<string, unknown>>) => void
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
}

const cellStyle: React.CSSProperties = {
  padding: '6px 10px', borderBottom: '1px solid var(--color-border, #eee)',
  fontSize: '0.9375rem', textAlign: 'left', whiteSpace: 'nowrap',
}

/** A row key as a heading when the form has none for it. */
export function humanizeKey(k: string): string {
  const t = String(k || '').replace(/[_\s]+/g, ' ').trim().toLowerCase()
  return t ? t[0].toUpperCase() + t.slice(1) : k
}

function display(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export default function SelectGridModal({ title, rows, columns, labels, onCancel, onApply }: Props) {
  const cols = useMemo(() => {
    if (columns && columns.length) return columns
    const seen: string[] = []
    for (const r of rows) {
      for (const k of Object.keys(r || {})) if (!seen.includes(k)) seen.push(k)
    }
    return seen
  }, [rows, columns])

  const [checked, setChecked] = useState<boolean[]>(() => rows.map(() => false))
  const allOn = checked.length > 0 && checked.every(Boolean)

  const toggle = (i: number) =>
    setChecked(prev => prev.map((c, idx) => (idx === i ? !c : c)))
  const toggleAll = () =>
    setChecked(rows.map(() => !allOn))

  const apply = () => onApply(rows.filter((_, i) => checked[i]))
  const selectedCount = checked.filter(Boolean).length

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div
        onClick={e => e.stopPropagation()}
        data-tw-dialog="select_grid"
        style={{
          background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)',
          borderRadius: 8, width: 'min(900px, 94vw)', maxHeight: '86vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 16px', borderBottom: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.9625rem' }}>{title || 'Select rows'}</span>
          <button
            onClick={onCancel}
            {...tw('action', 'close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1625rem', color: 'var(--color-text-secondary, #666)' }}
            title="Close">×</button>
        </div>

        <div style={{ overflow: 'auto', flex: 1 }}>
          {rows.length === 0 ? (
            <div style={{ padding: 24, color: 'var(--color-text-secondary, #666)', fontSize: '0.9625rem' }}>
              No rows returned.
            </div>
          ) : (
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...cellStyle, fontWeight: 600 }}>
                    <input type="checkbox" checked={allOn} onChange={toggleAll} />
                  </th>
                  {cols.map(c => (
                    <th key={c} style={{ ...cellStyle, fontWeight: 600 }}>
                      {labels?.[c] || labels?.[c.toLowerCase()] || humanizeKey(c)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    onClick={() => toggle(i)}
                    {...tw('row', i)}
                    style={{ cursor: 'pointer', background: checked[i] ? 'var(--color-accent-weak, rgba(76,141,255,0.16))' : 'transparent' }}>
                    <td style={cellStyle}>
                      <input type="checkbox" checked={checked[i]} onChange={() => toggle(i)} onClick={e => e.stopPropagation()} />
                    </td>
                    {cols.map(c => (
                      <td key={c} style={cellStyle}>{display(r[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 16px', borderTop: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <button
            onClick={onCancel}
            {...tw('action', 'cancel')}
            style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: '1px solid var(--color-border, #ccc)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text, #111)' }}>
            Cancel
          </button>
          <button
            onClick={apply}
            {...tw('action', 'apply')}
            disabled={selectedCount === 0}
            style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: 'none', background: 'var(--color-accent, #4c8dff)', color: '#fff', cursor: selectedCount === 0 ? 'not-allowed' : 'pointer', opacity: selectedCount === 0 ? 0.5 : 1 }}>
            Apply {selectedCount > 0 ? `(${selectedCount})` : ''}
          </button>
        </div>
      </div>
    </div>
  )
}
